/**
 * Geo targeting — a geo-type selector with a type-specific target input. Emits `{ geo_type, geo_data, radius }`;
 * the ad-group service formats `geo_data` (CSV for STATE/POSTAL_CODE/DMA, JSON for GEOFENCE, 'US' when empty).
 * GEOFENCE is a keyless JSON textarea for now (a map editor can replace it later).
 */

const GEO_TYPES = ['COUNTRY', 'STATE', 'POSTAL_CODE', 'DMA', 'GEOFENCE'] as const;
const RADII = ['10', '15', '25', '50', '75', '100', '125'];

export function GeoEditor(
  { geoType, geoData, radius, onChange }:
  { geoType: string; geoData: string; radius: string; onChange: (v: { geo_type: string; geo_data: string; radius: string }) => void },
): JSX.Element {
  const set = (patch: Partial<{ geo_type: string; geo_data: string; radius: string }>) =>
    onChange({ geo_type: geoType, geo_data: geoData, radius, ...patch });

  return (
    <div className="geo-editor">
      <div className="field">
        <label htmlFor="geo_type">Geo type</label>
        <select id="geo_type" value={geoType} onChange={(e) => set({ geo_type: e.target.value, geo_data: '' })}>
          {GEO_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {geoType === 'COUNTRY' && (
        <div className="field">
          <label htmlFor="geo_data">Country</label>
          <select id="geo_data" value={geoData || 'US'} onChange={(e) => set({ geo_data: e.target.value })}>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>
      )}

      {geoType === 'STATE' && (
        <div className="field full">
          <label htmlFor="geo_data">States</label>
          <input id="geo_data" type="text" value={geoData} placeholder="CA, NY, TX" onChange={(e) => set({ geo_data: e.target.value })} />
          <span className="hint">Comma-separated two-letter state codes.</span>
        </div>
      )}

      {geoType === 'POSTAL_CODE' && (
        <>
          <div className="field full">
            <label htmlFor="geo_data">ZIP codes</label>
            <input id="geo_data" type="text" value={geoData} placeholder="21013, 21020, 21030" onChange={(e) => set({ geo_data: e.target.value })} />
            <span className="hint">Comma-separated 5-digit ZIPs. Each is expanded by the radius below.</span>
          </div>
          <div className="field">
            <label htmlFor="radius">Radius (miles)</label>
            <select id="radius" value={radius || '10'} onChange={(e) => set({ radius: e.target.value })}>
              {RADII.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </>
      )}

      {geoType === 'DMA' && (
        <div className="field full">
          <label htmlFor="geo_data">DMA codes</label>
          <input id="geo_data" type="text" value={geoData} placeholder="501, 803" onChange={(e) => set({ geo_data: e.target.value })} />
          <span className="hint">Comma-separated 3-digit DMA codes.</span>
        </div>
      )}

      {geoType === 'GEOFENCE' && (
        <div className="field full">
          <label htmlFor="geo_data">Geofence polygons (JSON)</label>
          <textarea id="geo_data" rows={5} value={geoData} placeholder='[[[38.9,-77.0],[38.9,-77.1],[39.0,-77.05]]]' onChange={(e) => set({ geo_data: e.target.value })} />
          <span className="hint">A JSON array of polygons, each an array of [lat, lng] points.</span>
        </div>
      )}
    </div>
  );
}
